// services/timerSyncService.js
import { Api } from '@/core/api.js'
import store from '@/store'

const api = new Api()

export const timerSyncService = {
  // Cargar estado inicial desde backend y persistir
async loadInitialState(timerId) {
  try {
    const response = await api.get(`admin/timer-op/state/${timerId}`)

    // ACEPTAR: {data:{...}}, {data:...}, o payload plano {...}
    let payload = null
    if (response && typeof response === 'object') {
      if (response.data && typeof response.data === 'object') {
        // axios style: { data: {...} }  o  backend: { data: {...campos} }
        payload = response.data.data && typeof response.data.data === 'object'
          ? response.data.data
          : response.data
      } else {
        // fetch/objeto plano: {...}
        payload = response
      }
    }

    if (!payload || typeof payload !== 'object') {
      return null
    }

    // Convertir a formato frontend y persistir
    const frontendState = this.convertBackendToFrontendState(payload, timerId)

    this.saveToLocalStorage(timerId, frontendState)

    try {
      await store.dispatch('timer/saveManualTimerState', {
        timerId,
        stateData: frontendState
      })
    } catch (e) {
      console.warn('No se pudo propagar a Vuex (no es crítico):', e && e.message ? e.message : e)
    }

    return frontendState
  } catch (error) {
    return null
  }
},

  // Ejecutar acción PLAY en el servidor
  async playTimer(timerId, body) {
    try {
      const res = await api.post(`admin/timer-op/play/${timerId}`, body || {})

      // ✅ Marcar localmente que está corriendo desde ahora
      const now = Date.now()
      const local = this.loadFromLocalStorage(timerId) || {}
      // Determinar initial/target
      let target = Number(local.targetSeconds || local.initialSec || 0)
      if (!target) {
        // último recurso: minutos del timer en store
        try {
          const t = store.getters['timer/getTimerById'](timerId)
          if (t && t.durationMinutes) target = Number(t.durationMinutes) * 60
        } catch(_) {}
      }

      const stateData = {
        ...local,
        hasStarted: true,
        isRunning: true,
        isFinished: false,
        actionCompleted: false,
        runningSince: now,
        lastUpdated: now,
        targetSeconds: target || local.targetSeconds || 0,
        initialSec: target || local.initialSec || 0,
        totalElapsed: Number(local.totalElapsed || 0),
        remainingSec: Number(
          local.remainingSec != null ? local.remainingSec :
          (target ? Math.max(0, target - Number(local.totalElapsed || 0)) : 0)
        )
      }

      this.saveToLocalStorage(timerId, stateData)
      try {
        await store.dispatch('timer/saveManualTimerState', { timerId, stateData })
      } catch (_) {}

      return { ok: true, status: res && res.status, data: res && res.data }
    } catch (e) {
      const st = e && e.response ? e.response.status : 0
      if (st === 409) {
        // Ya corría en servidor: aun así fija runningSince local para avanzar en UI
        const now = Date.now()
        const local = this.loadFromLocalStorage(timerId) || {}
        const updated = { ...local, isRunning: true, hasStarted: true, runningSince: now, lastUpdated: now }
        this.saveToLocalStorage(timerId, updated)
        try { await store.dispatch('timer/saveManualTimerState', { timerId, stateData: updated }) } catch(_){}
        return { ok: false, status: 409 }
      }
      throw e
    }
  },

  // Ejecutar acción PAUSE en el servidor
async pauseTimer(timerId) {
  if (!navigator.onLine) {
    // Aún sin red, deja marcado localmente en pausa
    const now = Date.now()
    const local = this.loadFromLocalStorage(timerId) || {}
    const pivot = local.runningSince || local.lastUpdated || now
    const extra = Math.max(0, Math.floor((now - pivot) / 1000))
    const target = Number(local.targetSeconds || local.initialSec || 0)
    const total = Math.min(target || 0, Number(local.totalElapsed || 0) + extra)
    const remain = Math.max(0, (target || 0) - total)

    const updated = {
      ...local,
      isRunning: false,
      hasStarted: !!(total > 0),
      isFinished: false,          // sigue en curso pero pausado
      actionCompleted: false,
      totalElapsed: total,
      remainingSec: remain,
      targetSeconds: target || remain + total,
      initialSec: target || remain + total,
      runningSince: null,
      lastUpdated: now,
      fromServer: false
    }
    this.saveToLocalStorage(timerId, updated)
    try { await store.dispatch('timer/saveManualTimerState', { timerId, stateData: updated }) } catch(_) {}
    return { success: true, offline: true }
  }

  try {
    const employeeId = this.getEmployeeId()
    const postData = employeeId ? { employee_id: employeeId } : {}
    const response = await api.post(`admin/timer-op/pause/${timerId}`, postData)

    // ✅ Tras pausar en servidor, fijar pausa también en local
    const now = Date.now()
    const local = this.loadFromLocalStorage(timerId) || {}
    const pivot = local.runningSince || local.lastUpdated || now
    const extra = Math.max(0, Math.floor((now - pivot) / 1000))
    const target = Number(local.targetSeconds || local.initialSec || 0)
    const total = Math.min(target || 0, Number(local.totalElapsed || 0) + extra)
    const remain = Math.max(0, (target || 0) - total)

    const updated = {
      ...local,
      isRunning: false,
      hasStarted: !!(total > 0),
      isFinished: false,
      actionCompleted: false,
      totalElapsed: total,
      remainingSec: remain,
      targetSeconds: target || remain + total,
      initialSec: target || remain + total,
      runningSince: null,
      lastUpdated: now,
      fromServer: true
    }
    this.saveToLocalStorage(timerId, updated)
    try { await store.dispatch('timer/saveManualTimerState', { timerId, stateData: updated }) } catch (_) {}
    return response.data
  } catch (error) {
    return { success: false, error: error.message }
  }
},

  // Ejecutar acción STOP en el servidor
  async stopTimer(timerId) {
    if (!navigator.onLine) {
      return { success: true, offline: true }
    }

    try {
      const employeeId = this.getEmployeeId()
      const postData = employeeId ? { employee_id: employeeId } : {}

      const response = await api.post(`admin/timer-op/stop/${timerId}`, postData)
      return response.data
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // Ejecutar acción CHECK (firmar) en el servidor
  async checkTimer(timerId) {
    if (!navigator.onLine) {
      // Dejamos la normalización local tal como si hubiera ido al server
    } else {
      try {
        const employeeId = this.getEmployeeId()
        const postData = {
          ...(employeeId && { employee_id: employeeId }),
          check_date: new Date().toISOString().replace('T', ' ').substring(0, 19)
        }
        await api.post(`admin/timer-op/check/${timerId}`, postData)
      } catch (error) {
        console.warn('No se pudo ejecutar CHECK en servidor (continuamos local):', error)
      }
    }

    // ✅ Normalización local inmediata: estado base, listo para iniciar de nuevo
    const t = store.getters['timer/getTimerById'](timerId)
    const baseTarget = (t && t.durationMinutes ? Number(t.durationMinutes) * 60 : 0)
    const now = Date.now()

    const newState = {
      hasStarted: false,
      isRunning: false,
      isFinished: false,
      actionCompleted: true,   // quedó firmado ahora
      completedAt: now,

      targetSeconds: baseTarget,
      elapsedSeconds: 0,
      remainingSeconds: baseTarget,

      // compat CountDownManual
      remainingSec: baseTarget,
      totalElapsed: 0,
      initialSec: baseTarget,

      lastUpdated: now,
      fromServer: true
    }

    this.saveToLocalStorage(timerId, newState)
    try {
      await store.dispatch('timer/saveManualTimerState', { timerId, stateData: newState })
    } catch (_) {}

    // reflejar en listado
    const timers = structuredClone(store.state.timer.timers || [])
    const updated = timers.map(x => x.id === timerId ? {
      ...x,
      isAvailable: false,
      currentPendingAlarm: null,
      lastAlarmDone: now,
      remainingSec: baseTarget,
      totalElapsed: 0
    } : x)
    store.commit('timer/setTimers', updated)

    return { success: true }
  },

// Convertir formato del backend al formato del frontend (Vue 2 friendly)
// services/timerSyncService.js
convertBackendToFrontendState: function (backendState, timerId) {
  // Helpers
  function firstDefined() {
    for (let i = 0; i < arguments.length; i++) {
      const v = arguments[i]
      if (v !== undefined && v !== null) return v
    }
    return undefined
  }
  function toBool(x) { return x === true || x === 1 || x === '1' }

  // 1) Cargar estado LOCAL (para initialSec/runningSince)
  let savedLocal = null
  try {
    const all = JSON.parse(localStorage.getItem('manualTimersState') || '{}')
    savedLocal = all && all[timerId] ? all[timerId] : null
  } catch(_) {}

  // 2) initialSec: local > store > 0
  let initialSec = 0
  if (savedLocal && savedLocal.initialSec) {
    initialSec = Number(savedLocal.initialSec)
  } else {
    const timer = store.getters['timer/getTimerById'](timerId) // tu getter
    if (timer && timer.durationMinutes) {
      initialSec = Number(timer.durationMinutes) * 60
    }
  }

  // 3) Flags del backend
  let isFinished = toBool(backendState.needs_check)          // terminó y espera firma
  let actionCompleted = toBool(backendState.is_checked)      // firmado
  const serverIsRunning = toBool(backendState.is_running)    // corriendo seg. backend

  // 4) Segundos (prioridad backend, luego local)
  let targetSeconds = Number(firstDefined(
    backendState.target_seconds,
    backendState.total_seconds,
    backendState.duration_seconds,
    initialSec
  )) || 0

  let elapsedSeconds = Number(firstDefined(
    backendState.elapsed_seconds,
    (savedLocal ? savedLocal.totalElapsed : 0)
  )) || 0

  let remainingSeconds = firstDefined(
    (backendState.remaining_seconds != null ? Number(backendState.remaining_seconds) : null),
    (targetSeconds > 0 ? Math.max(0, targetSeconds - elapsedSeconds) : null),
    (savedLocal ? savedLocal.remainingSec : initialSec)
  )
  if (remainingSeconds == null) remainingSeconds = 0

  // 5) Normalización básica
  if (targetSeconds > 0 && remainingSeconds > targetSeconds) {
    remainingSeconds = targetSeconds
  }

  // 6) Status de control: preferir backend.status -> backend.last_status -> is_running
  const rawStatus = (backendState.status != null ? backendState.status : backendState.last_status)
  const status = Number(rawStatus != null ? rawStatus : (serverIsRunning ? 1 : 0))
  const RUNNING = (status === 1 || status === 4)
  const PAUSED  = (status === 2)
  const CHECKED = (status === 5) || actionCompleted === true

  // 7) CASOS TERMINALES (guard clauses) — retornan de inmediato

  // 7.a) Firmado/estado 5: resetear a estado base sin auto-run
  if (CHECKED) {
    const baseTarget = targetSeconds || initialSec || 0
    return {
      hasStarted: false,
      isRunning: false,
      isFinished: false,
      actionCompleted: false, // listo para nuevo ciclo

      targetSeconds: baseTarget,
      elapsedSeconds: 0,
      remainingSeconds: baseTarget,

      // Compat CountDownManual
      remainingSec: baseTarget,
      totalElapsed: 0,
      initialSec: baseTarget,

      lastUpdated: Date.now(),
      fromServer: true
    }
  }

  // 7.b) Pausa/estado 2: respetar pausa sin auto-run
  if (PAUSED) {
    const baseTarget = targetSeconds || initialSec || 0
    const baseRemain = (remainingSeconds != null) ? remainingSeconds : Math.max(0, baseTarget - elapsedSeconds)
    return {
      hasStarted: !!(elapsedSeconds > 0),
      isRunning: false,
      isFinished,            // respetar bandera del backend
      actionCompleted,       // respetar bandera del backend

      targetSeconds: baseTarget,
      elapsedSeconds,
      remainingSeconds: baseRemain,

      // Compat CountDownManual
      remainingSec: baseRemain,
      totalElapsed: elapsedSeconds,
      initialSec: baseTarget,

      lastUpdated: Date.now(),
      fromServer: true
    }
  }

  // 8) Corriendo: solo si el backend lo afirma (is_running) o status 1/4
  let isRunning = false
  if (serverIsRunning || RUNNING) {
    isRunning = true
    isFinished = false
    actionCompleted = false

    // Sumar delta si hay marca de tiempo
    const now = Date.now()
    if (backendState.started_at) {
      const startedMs = new Date(String(backendState.started_at).replace(' ', 'T')).getTime()
      if (!isNaN(startedMs)) {
        const base = Number(backendState.elapsed_before_start || 0)
        const extra = Math.max(0, Math.floor((now - startedMs) / 1000))
        elapsedSeconds = Math.max(elapsedSeconds, base + extra)
      }
    } else if (savedLocal && (savedLocal.runningSince || savedLocal.isRunning)) {
      const pivot = savedLocal.runningSince || savedLocal.lastUpdated
      if (pivot) {
        const extra = Math.max(0, Math.floor((now - pivot) / 1000))
        const base  = Math.max(Number(elapsedSeconds || 0), Number(savedLocal.totalElapsed || 0))
        elapsedSeconds = base + extra
      }
    }

    // Extender target si se agotó para no caer en "firmar" por remaining=0 estando corriendo
    if (targetSeconds <= 0) targetSeconds = Math.max(initialSec, elapsedSeconds)
    if (targetSeconds - elapsedSeconds <= 0) {
      const headroom = Math.max(60, Math.ceil((targetSeconds || initialSec || 60) * 0.5))
      targetSeconds = elapsedSeconds + headroom
    }
    remainingSeconds = Math.max(0, targetSeconds - elapsedSeconds)
  } else {
    // 9) Inactivo: no correr ni auto-correr
    isRunning = false
    // Si no queda tiempo pero hubo progreso, mostrar como terminado (a la espera de firma)
    if (remainingSeconds <= 0 && elapsedSeconds > 0) {
      isFinished = true
    }
  }

  // 10) Retorno final (compat CountDownManual)
  return {
    hasStarted: !!(isRunning || elapsedSeconds > 0),
    isRunning,
    isFinished,
    actionCompleted,

    targetSeconds,
    elapsedSeconds,
    remainingSeconds,

    // Compat CountDownManual
    remainingSec: remainingSeconds,
    totalElapsed: elapsedSeconds,
    initialSec: targetSeconds,

    lastUpdated: Date.now(),
    fromServer: true
  }
},
  // Obtener ID del empleado desde el store
  getEmployeeId() {
    try {
      // Diferentes formas de obtener el employee_id según tu implementación
      let employeeId = null

      if (store.getters['loginUser/getLocalEmployee']) {
        employeeId = store.getters['loginUser/getLocalEmployee']
      } else if (store.state.loginUser && store.state.loginUser.localEmployee) {
        employeeId = store.state.loginUser.localEmployee
      } else if (store.state.login && store.state.login.user) {
        employeeId = store.state.login.user.id
      }
      return employeeId
    } catch (error) {
      return null
    }
  },

  // Métodos helpers para localStorage
  saveToLocalStorage(timerId, stateData) {
    try {
      const manualTimersState = JSON.parse(localStorage.getItem('manualTimersState') || '{}')
      manualTimersState[timerId] = {
        ...stateData,
        lastUpdated: Date.now()
      }
      localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))
    } catch (error) {
      console.error('Error guardando en localStorage:', error)
    }
  },

  loadFromLocalStorage(timerId) {
    try {
      const manualTimersState = JSON.parse(localStorage.getItem('manualTimersState') || '{}')
      const state = manualTimersState[timerId] || null
      return state
    } catch (error) {
      console.error('Error cargando desde localStorage:', error)
      return null
    }
  },

  // Limpiar estado
  clearState(timerId) {
    try {
      const manualTimersState = JSON.parse(localStorage.getItem('manualTimersState') || '{}')
      delete manualTimersState[timerId]
      localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))
    } catch (error) {
      console.error('Error limpiando localStorage:', error)
    }
  }
}
